// src/lib/products.js
// ───────────────────────────────────────────────────────────────────────────
// 🟦 Motor de PRODUCTOS (catálogo reorder-ready) — PLAN-INTAKE-PRODUCTOS
// Crear/normalizar SKUs del catálogo `products`. El SKU lo ASIGNA EL SISTEMA
// (contador atómico en counters/products) → sin tipeo manual, sin colisiones,
// sin error humano. Cada SKU es rastreable por QR (ver src/utils/qrLabel.js).
// "Reutilizar, no duplicar": el intake y el Catálogo usan estos mismos helpers.
// ───────────────────────────────────────────────────────────────────────────
import { collection, addDoc, doc, updateDoc, runTransaction, serverTimestamp, getDocs, query, where } from "firebase/firestore";

// Prefijo legible por categoría (parte humana del SKU).
const SKU_PREFIX = { caja: "CAJ", display: "DIS", banner: "BAN", vinilo: "VIN", sticker: "STK", servicio: "SER", accesorio: "ACC" };
function skuPrefix(categoryId) {
  return SKU_PREFIX[categoryId] || (categoryId || "PRD").slice(0, 3).toUpperCase();
}

/** Construye el SKU a partir de la categoría y un número consecutivo. PURO (testeable).
 *  Ej: makeSku("caja", 42) -> "CAJ-00042" */
export function makeSku(categoryId, n) {
  return `${skuPrefix(categoryId)}-${String(n).padStart(5, "0")}`;
}

/** Payload del QR de un producto (lo lee el escáner). PURO. */
export function productQrPayload(product) {
  return JSON.stringify({ t: "product", id: product.id || null, sku: product.sku || null });
}

/** Reserva el siguiente número de SKU de forma atómica (crea el contador si no existe). */
async function nextSkuNumber(db) {
  const counterRef = doc(db, "counters", "products");
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(counterRef);
    const next = (snap.exists() ? snap.data().lastNumber || 0 : 0) + 1;
    if (snap.exists()) tx.update(counterRef, { lastNumber: next });
    else tx.set(counterRef, { lastNumber: next });
    return next;
  });
}

/** Normaliza el payload de un producto a la forma canónica del documento. PURO. */
export function buildProductDoc(p) {
  return {
    sku: p.sku || "",
    skuNumber: p.skuNumber || null,
    name: (p.name || "").trim(),
    code: (p.code || "").trim(),          // código/referencia del cliente o del componente (para dedupe)
    kind: p.kind === "componente" ? "componente" : "producto", // PLAN-INTAKE-FLOW: pista suave producto|componente
    bom: Array.isArray(p.bom) ? p.bom : [], // solo productos ensamblados: [{ code, componentId, qty }] (1 nivel)
    feedsInventory: p.feedsInventory !== false, // componente: ¿es parte real que entra a inventario? (default sí)
    categoryId: p.categoryId || "",
    clientId: p.clientId || null,
    imageUrl: p.imageUrl || "",
    artUrl: p.artUrl || "",                // arte 1UP (archivo de arte, distinto del preview)
    attributes: p.attributes || {},
    dieline: p.dieline || { fileUrl: "", notes: "" },
    lastPrice: typeof p.lastPrice === "number" ? p.lastPrice : null,
    lastJobId: p.lastJobId || null,
    version: p.version || 1,
    active: p.active !== false,
  };
}

/**
 * Crea un producto nuevo. El SKU se asigna automáticamente (contador atómico).
 * @return {Promise<{id:string, sku:string, skuNumber:number}>}
 */
export async function createProduct(db, payload, user) {
  if (!payload?.name?.trim()) throw new Error("El producto necesita un nombre.");
  if (!payload?.categoryId) throw new Error("El producto necesita una categoría.");
  const skuNumber = await nextSkuNumber(db);
  const sku = makeSku(payload.categoryId, skuNumber);
  const data = buildProductDoc({ ...payload, sku, skuNumber });
  const ref = await addDoc(collection(db, "products"), {
    ...data,
    createdAt: serverTimestamp(),
    createdBy: user?.uid || null,
  });
  return { id: ref.id, sku, skuNumber };
}

/**
 * Convierte los COMPONENTES de un trabajo en PRODUCTOS del catálogo (reorder-ready).
 * Dedupe por (cliente + código): si ya existe un producto de ese cliente con el mismo
 * `code`, lo ACTUALIZA (nombre/imagen/material); si no, lo CREA con SKU automático.
 * Solo procesa componentes con nombre Y código. @return {Promise<{created,updated,skipped}>}
 */
export async function upsertProductsFromComponents(db, { components = [], clientId = null, categoryId, lastJobId = null, user }) {
  if (!categoryId) throw new Error("Elige una categoría para los componentes.");
  const valid = components.filter((c) => (c?.name || "").trim() && (c?.code || "").trim());
  if (valid.length === 0) return { created: 0, updated: 0, skipped: components.length };

  // Productos existentes del MISMO cliente, para dedupe por código (sin índice compuesto).
  const qs = query(collection(db, "products"), where("clientId", "==", clientId || null));
  const snap = await getDocs(qs);
  const byCode = new Map(
    snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((p) => p.code).map((p) => [String(p.code).toLowerCase(), p])
  );

  let created = 0, updated = 0;
  for (const c of valid) {
    const found = byCode.get(String(c.code).toLowerCase());
    const attributes = { ...(found?.attributes || {}) };
    if (c.materialId) attributes.materialId = c.materialId;
    if (found) {
      await updateProduct(db, found.id, {
        ...found,
        name: c.name.trim() || found.name,
        code: c.code,
        imageUrl: c.imageUrl || found.imageUrl || "",
        attributes,
        lastJobId: lastJobId || found.lastJobId || null,
      });
      updated++;
    } else {
      await createProduct(db, {
        name: c.name.trim(), code: c.code, categoryId, clientId: clientId || null,
        imageUrl: c.imageUrl || "", attributes, lastJobId,
      }, user);
      created++;
    }
  }
  return { created, updated, skipped: components.length - valid.length };
}

/** Actualiza un producto existente (conserva su SKU; sube versión si se pide). */
export async function updateProduct(db, productId, payload) {
  const data = buildProductDoc(payload); // conserva sku/skuNumber que vienen en payload
  await updateDoc(doc(db, "products", productId), {
    ...data,
    version: (payload.version || 1) + (payload._bumpVersion ? 1 : 0),
    lastUpdatedAt: serverTimestamp(),
  });
}
